import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./client";

export interface Loaded<T> { data: T | null; error: ApiError | Error | null; loading: boolean; reload: () => Promise<void>; set: (next: T) => void }

/** Minimal data hook: runs `fn` on mount and whenever `deps` change; `reload` refetches quietly. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[]): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [loading, setLoading] = useState(true);
  const alive = useRef(true);
  const run = useCallback(async () => {
    try {
      const d = await fn();
      if (alive.current) {
        setData(d);
        setError(null);
      }
    } catch (e) {
      if (alive.current) setError(e as Error);
    } finally {
      if (alive.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    alive.current = true;
    setLoading(true);
    void run();
    return () => {
      alive.current = false;
    };
  }, [run]);
  return { data, error, loading, reload: run, set: setData };
}

/** Re-run `fn` every `ms` while `active`. */
export function usePoll(fn: () => void | Promise<void>, ms: number, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => void fn(), ms);
    return () => clearInterval(t);
  }, [fn, ms, active]);
}
